import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DatePicker, { registerLocale } from "react-datepicker";

import ru from "date-fns/locale/ru";
import toast, { Toaster } from "react-hot-toast";
import moment from "moment";
import clsx from "clsx";

import {
  actionGetExcelUnloadingsFilters,
  actionGetExcelUnloadings,
  actionGetExcelUnloadingsRetry,
  actionDeleteExcelUnloadings,
} from "../../../redux/action/unloadings/actionExcelUnloadings";
import {
  formatUnloadingsDate,
  formatUnloadingsTime,
} from "../../../utils/formatDate";
import {
  reducerExcelUnloadingsInputValues,
  reducerExcelUploadingStatus,
} from "../../../redux/reducer/unloadings/reducers/excelUnloadings";
import { createOptions } from "../../../utils/createOptions";
import { generateStyles } from "../../../utils/generateToastStyles";

import { AppDispatch } from "../../../redux/reducer/store";

import LimitSelect from "../../custom/limitSelect/limitSelect";
import Pagination from "../../pagination/pagination";
import Loader from "../../custom/loader/loader";
import Select from "../../custom/select/select";
import ToastPopup from "../../custom/toastPopup/toastPopup";

import RefreshIcon from "../../../assets/icons/refresh.svg";

import "../table.scss";
import { IUserOptions } from "../../../types/interfaces";

interface IExcelUnloadings {
  date: string;
  employee: string;
  order: { id: number };
  status: { [key: string]: string };
  time: string;
  type: string;
  customer?: string;
  url?: string;
}

const IS_VISIBLE_SIDEBAR = process.env.REACT_APP_IS_VISIBLE_SIDEBAR;

const statusesOptions = [
  { value: "", label: "Все" },
  { value: "Ordered", label: "В очереди" },
  { value: "Error", label: "Ошибка" },
  { value: "Done", label: "Сформирован" },
  { value: "Deleted", label: "Удален" },
  { value: "Empty", label: "Фото не найдено" },
];

const timeOptions = [
  { value: "", label: "Все" },
  { value: "1", label: "Больше минуты" },
  { value: "10", label: "Больше 10 минут" },
];

const TableAdminUnloadingExcel: FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    excelUnloadingsInputValues,
    excelUnloadingsStatus,
    excelUnloadingsCount,
    excelUnloadings,
    filters,
    limit,
    page,
  } = useSelector((state: any) => state.unloadings.excelUnloadings);
  const { isOpenSidebar } = useSelector(
    (state: IUserOptions) => state.user.user
  );

  const [selectedDate, setSelectedDate] = useState<Date | null>(
    excelUnloadingsInputValues.date && new Date(excelUnloadingsInputValues.date)
  );
  const [selectedStatusOption, setSelectedStatusOption] = useState<string>("");
  const [isPending, setIsPending] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [startPage, setStartPage] = useState<number>(1);

  const actualLimit =
    JSON.parse(localStorage.getItem("pageLimit") as string) || limit;

  const requestStatusOptions = [
    {
      status: 0,
      errorMessage: "Ошибка на стороне сервера.",
    },
  ];

  useEffect(() => {
    setIsPending(true);
    dispatch(actionGetExcelUnloadings(actualLimit, page)).then(() => {
      setIsPending(false);
    });
    dispatch(actionGetExcelUnloadingsFilters());
    registerLocale("ru", ru);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectChange = (
    event: { value: string; label: string },
    name: string
  ) => {
    const value = event.value;
    if (value === "") {
      if (name === "status") {
        setSelectedStatusOption("all");
      }
      const updatedInputValues = { ...excelUnloadingsInputValues };
      delete updatedInputValues[name];
      dispatch(reducerExcelUnloadingsInputValues(updatedInputValues));
    } else {
      dispatch(
        reducerExcelUnloadingsInputValues({
          ...excelUnloadingsInputValues,
          [name]:
            name === "moreThanMin"
              ? parseFloat(value)
              : name === "employee" || name === "customer"
              ? { id: value }
              : value,
        })
      );
    }

    setIsPending(true);
    dispatch(actionGetExcelUnloadings(limit, page)).then(() => {
      setIsPending(false);
    });
    resetPages();
  };

  const handleDatePickerChange = (date: Date) => {
    setSelectedDate(date);

    const formattedDate = moment(date).format("YYYY-MM-DD");

    dispatch(
      reducerExcelUnloadingsInputValues({
        ...excelUnloadingsInputValues,
        date: formattedDate,
      })
    );

    setIsPending(true);
    dispatch(actionGetExcelUnloadings(limit, page)).then(() => {
      setIsPending(false);
    });
  };

  const handleDateKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.preventDefault();
  };

  const handleClearDate = () => {
    if (selectedDate) {
      setSelectedDate(null);

      dispatch(
        reducerExcelUnloadingsInputValues({
          ...excelUnloadingsInputValues,
          date: "",
        })
      );

      setIsPending(true);
      dispatch(actionGetExcelUnloadings(limit, page)).then(() => {
        setIsPending(false);
      });
    }
  };

  const resetPages = () => {
    setCurrentPage(1);
    setStartPage(1);
  };

  const handleChange = (newLimit: number, newPage: number) => {
    setIsPending(true);
    dispatch(actionGetExcelUnloadings(newLimit, newPage)).then(() => {
      setIsPending(false);
    });
    localStorage.setItem("pageLimit", JSON.stringify(newLimit));
  };

  const handleChangeLimits = (option: { value: number }) => {
    handleChange(option.value, 1);
    resetPages();
  };

  const handleClickRefreshBtn = () => {
    setIsPending(true);
    dispatch(actionGetExcelUnloadings(limit, page)).then(() => {
      setIsPending(false);
    });
  };

  const handleClickDeleteBtn = (id: number) => {
    setIsPending(true);
    dispatch(actionDeleteExcelUnloadings(id)).then(() => {
      dispatch(actionGetExcelUnloadings(limit, page)).then(() => {
        setIsPending(false);
      });
    });
  };

  useEffect(() => {
    requestStatusOptions.forEach((option: any) => {
      if (excelUnloadingsStatus === option.status) {
        toast.dismiss();

        toast(
          (t) => (
            <ToastPopup
              title={
                option.successMessage
                  ? option.successMessage
                  : option.errorMessage
              }
              isSuccess={option.successMessage ? true : false}
              toast={toast}
              t={t}
            />
          ),
          generateStyles(option.successMessage ? true : false)
        );
      }
    });

    setTimeout(() => {
      dispatch(reducerExcelUploadingStatus(null));
    }, 5000);
    // eslint-disable-next-line
  }, [dispatch, excelUnloadingsStatus]);

  return (
    <>
      <div className="loader__wrapper">
        {(!excelUnloadings || isPending) && <Loader />}
      </div>
      <div
        className={clsx(
          JSON.parse(IS_VISIBLE_SIDEBAR as string) &&
            isOpenSidebar &&
            "active__table",
          !isOpenSidebar && "closed__table",
          "table"
        )}
      >
        {excelUnloadingsStatus && excelUnloadingsStatus !== null && (
          <Toaster
            position="top-center"
            toastOptions={{
              duration: 5000,
            }}
          />
        )}

        <div className="filters__container__mobile">
          <div className="filters__item__mobile">
            <div className="filter__data_picker filter__data_picker_excel">
              <DatePicker
                onChange={handleDatePickerChange}
                onKeyDown={handleDateKeyDown}
                selected={selectedDate}
                dateFormat="dd.MM.yyyy"
                placeholderText="Дата"
                autoComplete="off"
                locale="ru"
                name="date"
              />
              {selectedDate && (
                <div className="clear__icon" onClick={handleClearDate} />
              )}
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <Select
                defaultValue={
                  excelUnloadingsInputValues.type &&
                  createOptions(filters, "types").find(
                    (option: { value: string; label: string }) =>
                      option.value === excelUnloadingsInputValues.type
                  )
                }
                propsChange={(event: { value: string; label: string }) =>
                  selectChange(event, "type")
                }
                isShowPlaceholder={
                  !!excelUnloadingsInputValues.type ||
                  excelUnloadingsInputValues.type === undefined
                }
                options={createOptions(filters, "types")}
                className="users__filter"
                placeholder="Тип"
              />
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <Select
                defaultValue={
                  excelUnloadingsInputValues.employee &&
                  createOptions(filters, "employees")?.find(
                    (option: { value: string; label: string }) =>
                      option.value === excelUnloadingsInputValues.employee.id
                  )
                }
                propsChange={(event: { value: string; label: string }) =>
                  selectChange(event, "employee")
                }
                isShowPlaceholder={
                  (excelUnloadingsInputValues?.employee &&
                    !!Object.keys(excelUnloadingsInputValues?.employee)
                      .length) ||
                  excelUnloadingsInputValues?.employee === undefined
                }
                options={createOptions(filters, "employees")}
                className="users__filter"
                placeholder="Сотрудник"
              />
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <Select
                defaultValue={
                  excelUnloadingsInputValues.customer &&
                  createOptions(filters, "customers")?.find(
                    (option: { value: string; label: string }) =>
                      option.value === excelUnloadingsInputValues.customer.id
                  )
                }
                propsChange={(event: { value: string; label: string }) =>
                  selectChange(event, "customer")
                }
                isShowPlaceholder={
                  (excelUnloadingsInputValues?.customer &&
                    !!Object.keys(excelUnloadingsInputValues?.customer)
                      .length) ||
                  excelUnloadingsInputValues?.customer === undefined
                }
                options={createOptions(filters, "customers")}
                className="users__filter"
                placeholder="Клиент"
              />
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <Select
                defaultValue={
                  excelUnloadingsInputValues.status &&
                  statusesOptions.find(
                    (option: { value: string; label: string }) =>
                      option.value === excelUnloadingsInputValues.status
                  )
                }
                propsChange={(event: { value: string; label: string }) =>
                  selectChange(event, "status")
                }
                isShowPlaceholder={
                  !!excelUnloadingsInputValues.status ||
                  selectedStatusOption === "all"
                }
                options={statusesOptions}
                className="users__filter"
                placeholder="Статус"
              />
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <Select
                defaultValue={
                  excelUnloadingsInputValues.moreThanMin &&
                  timeOptions.find(
                    (option: { value: string; label: string }) =>
                      parseFloat(option.value) ===
                      excelUnloadingsInputValues.moreThanMin
                  )
                }
                propsChange={(event: { value: string; label: string }) => {
                  selectChange(event, "moreThanMin");
                }}
                isShowPlaceholder={
                  !!excelUnloadingsInputValues.moreThanMin ||
                  excelUnloadingsInputValues.moreThanMin === undefined
                }
                placeholder="Время выполнения"
                className="users__filter"
                options={timeOptions}
              />
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="refresh__btn" onClick={handleClickRefreshBtn}>
              <img className="refresh__icon" src={RefreshIcon} alt="refresh" />
            </div>
          </div>
        </div>

        <div className="table__wrapper">
          <table>
            <thead className="table__header__filters">
              <tr className="unloadings__filter_tr">
                <th className="reports__number_th">№</th>
                <th>
                  <div className="filter__data_picker filter__data_picker_excel">
                    <DatePicker
                      onChange={handleDatePickerChange}
                      onKeyDown={handleDateKeyDown}
                      selected={selectedDate}
                      dateFormat="dd.MM.yyyy"
                      placeholderText="Дата"
                      autoComplete="off"
                      locale="ru"
                      name="date"
                    />
                    {selectedDate && (
                      <div className="clear__icon" onClick={handleClearDate} />
                    )}
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <Select
                      defaultValue={
                        excelUnloadingsInputValues.type &&
                        createOptions(filters, "types").find(
                          (option: { value: string; label: string }) =>
                            option.value === excelUnloadingsInputValues.type
                        )
                      }
                      propsChange={(event: { value: string; label: string }) =>
                        selectChange(event, "type")
                      }
                      isShowPlaceholder={
                        !!excelUnloadingsInputValues.type ||
                        excelUnloadingsInputValues.type === undefined
                      }
                      options={createOptions(filters, "types")}
                      className="users__filter"
                      placeholder="Тип"
                    />
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <Select
                      defaultValue={
                        excelUnloadingsInputValues.employee &&
                        createOptions(filters, "employees")?.find(
                          (option: { value: string; label: string }) =>
                            option.value ===
                            excelUnloadingsInputValues.employee.id
                        )
                      }
                      propsChange={(event: { value: string; label: string }) =>
                        selectChange(event, "employee")
                      }
                      isShowPlaceholder={
                        (excelUnloadingsInputValues?.employee &&
                          !!Object.keys(excelUnloadingsInputValues?.employee)
                            .length) ||
                        excelUnloadingsInputValues?.employee === undefined
                      }
                      options={createOptions(filters, "employees")}
                      className="users__filter"
                      placeholder="Сотрудник"
                    />
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <Select
                      defaultValue={
                        excelUnloadingsInputValues.customer &&
                        createOptions(filters, "customers")?.find(
                          (option: { value: string; label: string }) =>
                            option.value ===
                            excelUnloadingsInputValues.customer.id
                        )
                      }
                      propsChange={(event: { value: string; label: string }) =>
                        selectChange(event, "customer")
                      }
                      isShowPlaceholder={
                        (excelUnloadingsInputValues?.customer &&
                          !!Object.keys(excelUnloadingsInputValues?.customer)
                            .length) ||
                        excelUnloadingsInputValues?.customer === undefined
                      }
                      options={createOptions(filters, "customers")}
                      className="users__filter"
                      placeholder="Клиент"
                    />
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <Select
                      defaultValue={
                        excelUnloadingsInputValues.status &&
                        statusesOptions.find(
                          (option: { value: string; label: string }) =>
                            option.value === excelUnloadingsInputValues.status
                        )
                      }
                      propsChange={(event: { value: string; label: string }) =>
                        selectChange(event, "status")
                      }
                      isShowPlaceholder={
                        !!excelUnloadingsInputValues.status ||
                        selectedStatusOption === "all"
                      }
                      options={statusesOptions}
                      className="users__filter"
                      placeholder="Статус"
                    />
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <Select
                      defaultValue={
                        excelUnloadingsInputValues.moreThanMin &&
                        timeOptions.find(
                          (option: { value: string; label: string }) =>
                            parseFloat(option.value) ===
                            excelUnloadingsInputValues.moreThanMin
                        )
                      }
                      propsChange={(event: {
                        value: string;
                        label: string;
                      }) => {
                        selectChange(event, "moreThanMin");
                      }}
                      isShowPlaceholder={
                        !!excelUnloadingsInputValues.moreThanMin ||
                        excelUnloadingsInputValues.moreThanMin === undefined
                      }
                      placeholder="Время выполнения"
                      className="users__filter"
                      options={timeOptions}
                    />
                  </div>
                </th>
                <th className="unloadings__link_cell">Ссылка</th>
                <th>
                  <div className="refresh__btn" onClick={handleClickRefreshBtn}>
                    <img
                      className="refresh__icon"
                      src={RefreshIcon}
                      alt="refresh"
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {excelUnloadings &&
                excelUnloadings.map((elem: IExcelUnloadings, index: number) => (
                  <tr key={index}>
                    <td>{elem?.order?.id}</td>
                    <td>{formatUnloadingsDate(elem?.date)}</td>
                    <td>{elem?.type}</td>
                    <td>{elem?.employee}</td>
                    <td>{elem?.customer}</td>
                    <td>{Object.values(elem.status)}</td>
                    <td>{formatUnloadingsTime(elem.time)}</td>
                    <td>
                      {Object.keys(elem.status).toString() === "Done" && (
                        <a href={elem?.url} download className="report__link">
                          Скачать
                        </a>
                      )}
                      {Object.keys(elem.status).toString() === "Error" && (
                        <span
                          className="report__link"
                          onClick={() =>
                            dispatch(actionGetExcelUnloadingsRetry(elem.order))
                          }
                        >
                          Повторить
                        </span>
                      )}
                      {elem.status &&
                        Object.keys(elem.status).toString() === "Ordered" && (
                          <span
                            className="report__link"
                            onClick={() => handleClickDeleteBtn(elem.order.id)}
                          >
                            Отмена
                          </span>
                        )}
                    </td>
                    <td></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <div className="pagination__count">Всего: {excelUnloadingsCount}</div>
          <div className="pagination__str">
            <span>Отображать строки</span>
            <LimitSelect limit={actualLimit} propsChange={handleChangeLimits} />
          </div>
          <Pagination
            setCurrentPage={setCurrentPage}
            count={excelUnloadingsCount}
            setStartPage={setStartPage}
            handleChange={handleChange}
            currentPage={currentPage}
            startPage={startPage}
            limit={actualLimit}
            pageName="userReports"
          />
        </div>
      </div>
    </>
  );
};

export default TableAdminUnloadingExcel;
